<script setup lang="ts">
import { capitalize } from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { VBadge, VTable } from '@/modules/shared/components'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { unflattenKey } from '@/modules/shared/utils/v-table'
import OtherEntityNav from '../components/other-entity-nav.vue'
import OtherEntityHeader from '../components/other-entity-header.vue'
import { useOtherEntityStore } from '../stores/other-entity-store'

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()
const otherEntityStore = useOtherEntityStore()

const id = route.params.other_entity_id
const cid = computed(() => `funding-entity:${id}`)
const currentOtherEntity = computed(() => otherEntityStore.items.get(cid.value))
const skeleton = ref(true)

const isAdmin = computed(
  () =>
    authStore.is_site_or_group_admin ||
    currentOtherEntity.value?.admins
      ?.map((admin) => admin._cid)
      .includes(`individual:${authStore.current_user?.investor_id}`),
)

const other_entities = computed(() =>
  otherEntityStore.other_entities.filter((other_entity) => other_entity.id !== currentOtherEntity.value?.id),
)

const canEditEntity = (item) =>
  authStore.is_site_or_group_admin ||
  unflattenKey(item, 'admins')?.some(
    (admin) => admin.id?.toString() === authStore.current_user?.investor_id?.toString(),
  )

onMounted(async () => {
  await otherEntityStore.fetchOtherEntities('funding-entity', route.params.other_entity_id)
  await otherEntityStore.fetchOtherEntity(`${id}`)
  skeleton.value = false
})
</script>

<template>
  <TheLayout>
    <OtherEntityHeader />
    <OtherEntityNav />
    <VTable
      :columns="[
        {
          key: 'name',
          name: capitalize(t('shared.name')),
          type: 'string',
          align: 'left',
          is_visible: true,
          fixed: true,
        },
        {
          key: 'admins',
          name: capitalize(t('shared.admin', 0)),
          type: 'string',
          align: 'left',
          is_visible: true,
        },
      ]"
      :items="other_entities"
      :name="`other-entity-associated-entities-${route.params.slug}-${cid}`"
      :skeleton="skeleton"
      :slots="['admins', 'name']"
      class="mt-6"
    >
      <template #admins="{ item }">
        <span v-for="(admin, index) in unflattenKey(item, 'admins')" :key="admin.id">
          <RouterLink
            v-if="authStore.is_site_or_group_admin"
            class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
            :to="{ name: 'investing.individual-overview', params: { individual_id: admin.id } }"
          >
            {{ admin.name }}
          </RouterLink>
          <span class="text-gray-900" v-else>{{ admin.name }}</span>
          <span v-if="index !== unflattenKey(item, 'admins').length - 1">, </span>
        </span>
      </template>
      <template #name="{ item }">
        <span class="flex items-start gap-4">
          <RouterLink
            v-if="isAdmin"
            class="cursor-pointer whitespace-break-spaces font-medium text-[#51889B] underline decoration-[#51889B]/50"
            :to="{ name: 'investing.other-entity.overview', params: { other_entity_id: item.id } }"
          >
            {{ item.name }}
          </RouterLink>
          <span class="text-gray-900" v-else>{{ item.name }}</span>
          <RouterLink
            v-if="item.profile_status === 'incomplete' && canEditEntity(item)"
            :to="{ name: 'investing.other-entity.edit', params: { other_entity_id: item.id } }"
          >
            <VBadge color="red" size="xxs" class="w-fit cursor-pointer whitespace-nowrap">Profile Incomplete</VBadge>
          </RouterLink>
          <VBadge
            v-else-if="item.profile_status === 'incomplete'"
            color="red"
            size="xxs"
            class="w-fit whitespace-nowrap"
            >Profile Incomplete</VBadge
          >
        </span>
      </template>
    </VTable>
  </TheLayout>
</template>
